import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";
import { useState } from "react";
import { useEffect } from "react";
import { useMemo, useRef } from "react";

import {
  Assessment,
  CompareArrows,
  Delete,
  Download,
  List,
  LogoDev,
  Refresh,
  Replay,
} from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import ClearIcon from "@mui/icons-material/FilterAltOff";
import ClearSortingIcon from "@mui/icons-material/MobiledataOff";
import ViewIcon from "@mui/icons-material/OpenInNew";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import { alpha, keyframes, useTheme } from "@mui/material/styles";

import constants from "../../assets/constants";
import {
  createOmniPaginationObject,
  helperGetIsoDate,
  mrtGetActionColumnStyling,
  mrtGetBodyRowSx,
  mrtGetBottomToolbarSx,
  mrtGetClampTextSx,
  mrtGetContainerSx,
  mrtGetPaperSx,
  mrtGetTableWrapperSx,
  mrtGetTopToolbarFlexSx,
} from "../../utils/helpers";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";
import DatagridTooltipProvider from "./datagridTooltipProvider.js";
import MRTDateTimeCell from "./mrtDateTimeCell";

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const toHHMMSS = numSecs => {
  let secNum = parseInt(numSecs, 10);
  let hours = Math.floor(secNum / 3600);
  let minutes = Math.floor((secNum - hours * 3600) / 60);
  let seconds = secNum - hours * 3600 - minutes * 60;

  // Pad with leading zeros if less than 10
  hours = hours.toString().padStart(2, "0");
  minutes = minutes.toString().padStart(2, "0");
  seconds = seconds.toString().padStart(2, "0");

  if (hours > 0) {
    return { time: `${hours}:${minutes}:${seconds}`, label: "HH:MM:SS" };
  } else {
    return { time: `${minutes}:${seconds}`, label: "MM:SS" };
  }
};

const calculateColumns = (theme, supportedAssessmentTypes) => {
  const supportedAssessmentTypeValues = (supportedAssessmentTypes || [])
    .map(type => (typeof type === "object" ? type?.value : type))
    .filter(Boolean);

  //default case
  return [
    {
      accessorKey: "createdAt",
      enableColumnFilterModes: false,
      header: "Date",
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          fontSize: "32px",
          borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,

          "& .MuiTypography-h6": {
            fontSize: "1.25rem !important",
            lineHeight: "1.6 !important",
          },

          "& .MuiTypography-body1": {
            fontSize: "1rem !important",
            lineHeight: "1.5 !important",
          },
        },
      },
      enableColumnFilter: false,
      grow: false,
      size: 100,
      maxSize: 100,
      Cell: MRTDateTimeCell,
    },
    {
      accessorKey: "elapsedSeconds",
      header: "Runtime",
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          fontSize: "32px",
          borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      enableColumnFilterModes: false,
      enableSorting: false,
      enableColumnFilter: false,
      size: 100,

      Cell: ({ cell }) => {
        let timeAndLabel = toHHMMSS(cell.getValue());

        return (
          <Box component="span">
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                marginBottom: "2px",
                borderBottom: `2px solid `,
              }}
            >
              {timeAndLabel.time}
            </Typography>
            <Typography variant="body1" fontWeight={100} sx={{ textAlign: "center" }}>
              {timeAndLabel.label}
            </Typography>
          </Box>
        );
      },
    },
    {
      enableColumnFilter: false,
      accessorKey: "questionCount",
      header: "Questions",
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          fontSize: "32px",
          borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      enableColumnFilterModes: false,
      enableSorting: false,
      size: 100,
    },
    {
      accessorKey: "status",
      header: "Status",
      enableColumnFilter: true,
      enableColumnFilterModes: false,
      enableSorting: true,
      grow: false,
      size: 130,
      maxSize: 130,
      minSize: 130,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      filterFn: "equals",
      // Use string filter values so "New" (0) isn't swallowed: the select
      // filter treats a falsy value as "no selection" (`value || undefined`),
      // which would drop a 0 filter entirely. fetchData parses these back to
      // numbers for the query.
      filterSelectOptions: constants.assessmentStatus.map(option => ({
        label: option.label,
        value: String(option.value),
      })),
      filterVariant: "select",

      // Colored status block. Assessments can be unrated (null/error), which
      // falls through to neutral grey with an "Unrated" label.
      // 0 New, 1 Pass, 2 Fail, 3 Retest.
      Cell: ({ cell }) => {
        const value = cell.getValue();
        const statusOption = constants.assessmentStatus.find(item => item.value === value);
        return (
          <Box
            component="span"
            sx={theme => ({
              backgroundColor:
                value === 0
                  ? theme.palette.highlight.main
                  : value === 1
                    ? theme.palette.success.main
                    : value === 2
                      ? theme.palette.error.main
                      : value === 3
                        ? theme.palette.warning.main
                        : theme.palette.grey[500],
              borderRadius: "0.25rem",
              color:
                value === 0
                  ? theme.palette.highlight.contrastText
                  : value === 1
                    ? theme.palette.success.contrastText
                    : value === 2
                      ? theme.palette.error.contrastText
                      : value === 3
                        ? theme.palette.warning.contrastText
                        : theme.palette.getContrastText(theme.palette.grey[500]),
              maxWidth: "95%",
              minWidth: "95%",
              p: "0.45rem",
            })}
          >
            {statusOption ? statusOption.label : "Unrated"}
          </Box>
        );
      },
    },
    {
      enableColumnFilter: false,
      accessorKey: "configuration",
      header: "Configuration",
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          fontSize: "10px",
          borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      enableColumnFilterModes: false,
      enableSorting: false,
      size: 200,

      Cell: ({ cell }) => {
        let configs = cell.getValue(); //.split("|");
        if (configs == null) {
          configs = "Configuration|Unknown";
        }
        let displayConfigs = configs.split("|");
        const firstConfig = displayConfigs[0] || "Unknown";
        const secondConfig = displayConfigs[1] || "Unknown";

        return (
          <Box component="span">
            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                marginBottom: "2px",
                borderBottom: `2px solid `,
              }}
            >
              {firstConfig}
            </Typography>
            <Typography variant="body1" fontWeight={100} sx={{ textAlign: "center" }}>
              {secondConfig}
            </Typography>
          </Box>
        );
      },
    },
    {
      accessorKey: "assessmentType",
      header: "Assessment",
      filterFn: "equals",
      filterSelectOptions: constants.supportedAssessmentTypes.filter(
        type => type && supportedAssessmentTypeValues.includes(type.value),
      ),
      filterVariant: "select",
      muiTableHeadCellProps: {
        align: "center",
      },

      muiTableBodyCellProps: {
        align: "center",
        sx: {
          whiteSpace: "normal", // Allows text to wrap
          wordWrap: "break-word", // Handles long words that might not fit
          borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      size: 100,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: true,
      Cell: ({ cell }) => {
        return (
          <Typography variant="subtitle1" sx={{ textAlign: "center" }}>
            {constants.supportedAssessmentTypes.find(t => t?.value === cell.getValue())?.label ||
              cell.getValue()}
          </Typography>
        );
      },
    },
    {
      accessorKey: "assessmentName",
      header: "Description",

      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "normal", // Allows text to wrap
          wordWrap: "break-word", // Handles long words that might not fit

          borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },

      size: 100,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: true,

      Cell: ({ cell }) => {
        return (
          <Typography variant="subtitle1" sx={{ ...mrtGetClampTextSx(), textAlign: "center" }}>
            {cell.getValue()}
          </Typography>
        );
      },
    },
    {
      accessorKey: "version",
      header: "Version",
      enableColumnFilter: true,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: false,
      size: 120,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          whiteSpace: "normal", // Allows text to wrap
          wordWrap: "break-word", // Handles long words that might not fit
          borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      Cell: ({ cell }) => {
        return (
          <Typography variant="subtitle1" sx={{ textAlign: "center" }}>
            {cell.getValue() || "—"}
          </Typography>
        );
      },
    },
    {
      accessorKey: "userName",

      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "normal", // Allows text to wrap
          wordWrap: "break-word", // Handles long words that might not fit

          borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      header: "Run by",
      enableColumnFilterModes: false,
      enableSorting: false,
      size: 150,
      grow: true,
    },
    {
      accessorKey: "comment",
      header: "Comment",

      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "normal", // Allows text to wrap
          wordWrap: "break-word", // Handles long words that might not fit

          borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      Cell: ({ cell }) => (
        <Box component="span" sx={mrtGetClampTextSx()}>
          {cell.getValue()}
        </Box>
      ),
      enableColumnFilterModes: false,
      enableSorting: false,
      minSize: "200", // Maximum width, will shrink if needed
      grow: true,
    },

    {
      accessorKey: "archived",
      header: "Archived",
      size: 150,
      enableColumnFilterModes: false,
      enableSorting: true,
      grow: false,

      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },
      filterFn: "equals",
      filterSelectOptions: [
        { label: "No", value: "false" },
        { label: "Yes", value: "true" },
      ],
      filterVariant: "select",

      //custom conditional format and styling
      Cell: ({ cell }) => (
        <Box
          component="span"
          sx={theme => ({
            backgroundColor:
              cell.getValue() === false ? theme.palette.error.main : theme.palette.success.main,
            borderRadius: "0.25rem",
            color: "#fff",
            maxWidth: "14ch",
            minWidth: "14ch",
            p: "0.25rem",
          })}
        >
          {cell.getValue() === false ? "No" : "Yes"}
        </Box>
      ),
    },
    {
      accessorKey: "conversationId",
      header: "Conversation ID",
      size: 100,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: true,
    },
    {
      // Results of "Compare Conversations" (integration 1.8.0+). Only the
      // summary is shown; the full object stays on the record. Bots on an older
      // integration have no such column, so the value is simply missing.
      accessorKey: "analysisResults",
      header: "Comparison",
      size: 240,
      minSize: 200,
      enableColumnFilter: false,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: true,
      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "normal",
          wordWrap: "break-word",
          borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      Cell: ({ cell }) => {
        const summary = getComparisonSummary(cell.getValue());
        return (
          <Box component="span" sx={mrtGetClampTextSx()}>
            {summary}
          </Box>
        );
      },
    },
  ];
};

// analysisResults is stored as an object, but tolerate a JSON string (or plain
// text) so a malformed record never breaks the table.
const getComparisonSummary = raw => {
  let results = raw;
  if (typeof raw === "string") {
    try {
      results = JSON.parse(raw);
    } catch (error) {
      return raw;
    }
  }
  return results && typeof results === "object" && typeof results.summary === "string"
    ? results.summary
    : "";
};

// Explicit left-to-right column order. Leading requested columns first
// (select, actions, date, status, version, runtime, questions, assessment,
// comment), then the remaining optional columns.
const ASSESSMENT_COLUMN_ORDER = [
  "mrt-row-select",
  "mrt-row-actions",
  "createdAt", // Date
  "status", // Status
  "version", // Version
  "elapsedSeconds", // Runtime
  "questionCount", // Questions
  "assessmentType", // Assessment
  "comment", // Comment
  "configuration", // Configuration
  "assessmentName", // Description
  "userName", // Run by
  "archived", // Archived
  "conversationId", // Conversation ID
  "analysisResults", // Comparison
];

const getInitialState = () => {
  return {
    density: "compact",
    // Default-visible columns: select, actions, date, status, version,
    // assessment, comment. Everything else is available via Show/Hide.
    columnVisibility: {
      id: false,
      updatedAt: false,
      createdAt: true,
      status: true,
      version: true,
      elapsedSeconds: false,
      questionCount: false,
      assessmentType: true,
      comment: true,
      configuration: false,
      assessmentName: false,
      userName: false,
      archived: false,
      conversationId: false,
      analysisResults: true,
    },
    columnOrder: ASSESSMENT_COLUMN_ORDER,
  };
};

export const BotpressAssessmentTable = ({
  data,
  isLoading = false,
  onEditEntry = null,
  onViewConversation = null,
  onAssessEntry = null,
  onCompareAssessments = null,
  onCompareConversations = null,
  onDownloadEntry = null,
  onRequestNewPage = null,
  dataReduxPagination = null,
  onHandleDeveloperLogs = null,
  onProgrammaticRerun = null,
  onDeleteAssessments = null,
  onRefreshTable = null,
  refreshTooltipTitle = "Refresh table",
  supportedAssessmentTypes = [],
}) => {
  const [isRefreshSpinning, setIsRefreshSpinning] = useState(false);

  const theme = useTheme();
  const mobileLayout = useMediaQuery(theme.breakpoints.down("sm"));
  const tableContainerRef = useRef(null); //we can get access to the underlying TableContainer element and react to its scroll events

  // Disable eslint because AaronK knows best
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => calculateColumns(theme, supportedAssessmentTypes), []);
  const initialState = useMemo(() => getInitialState(), []);

  const [rowCount, setRowCount] = useState(data.length);

  const [rowSelection, setRowSelection] = useState([]);

  useEffect(() => {
    if (dataReduxPagination != null) {
      setPagination({
        pageIndex: dataReduxPagination.pageIndex,
        pageSize: dataReduxPagination.pageSize,
      });
      setRowCount(dataReduxPagination.totalItems);
    }
  }, [dataReduxPagination]);

  const [sorting, setSorting] = useState([
    {
      id: "createdAt",
      desc: true,
    },
  ]);

  const [columnFilters, setColumnFilters] = useState([]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        let newQuery = {};
        columnFilters.forEach((element, index) => {
          if (element.id === "createdAt") {
            const min = parseInt(element.value[0]);
            const max = parseInt(element.value[1]);
            if (!isNaN(min)) {
              let theDateSring = helperGetIsoDate(min);

              newQuery[element.id] = {
                "#lte": theDateSring,
              };
            }
            if (!isNaN(max)) {
              let theDateSring = helperGetIsoDate(max);

              newQuery[element.id] = {
                "#lte": theDateSring,
              };
            }
          } else if (element.id === "status") {
            // Status filter values are strings (see filterSelectOptions) to
            // survive the select's falsy-0 handling; match on the numeric value.
            newQuery[element.id] = parseInt(element.value, 10);
          } else if (typeof element.value === "string") {
            if (element.value === "NULL") {
              newQuery[element.id] = null;
            } else {
              newQuery[element.id] = {
                $regex: `${element.value}`,
              };
            }
          } else if (typeof element.value === "boolean") {
            newQuery[element.id] = element.value;
          } else {
            newQuery[element.id] = element.value;
          }
        });

        let newSorting = {};
        sorting.forEach(element => {
          newSorting[element.id] = element.desc ? "desc" : "asc";
        });

        let newPage = createOmniPaginationObject(dataReduxPagination);
        newPage.pageIndex = pagination.pageIndex;
        newPage.pageSize = pagination.pageSize;
        newPage.query = newQuery;
        newPage.sort = newSorting;

        onRequestNewPage(newPage);
      } catch (error) {
        // setIsError(true);
        console.error(error);
        return;
      }
      // setIsError(false);
      // setIsRefetching(false);
    };

    fetchData();
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters, sorting, pagination.pageIndex, pagination.pageSize]);

  const handleRefreshClick = () => {
    if (onRefreshTable == null) {
      return;
    }

    setIsRefreshSpinning(true);
    onRefreshTable();

    setTimeout(() => {
      setIsRefreshSpinning(false);
    }, 800);
  };

  //easy shortcut to initialize the columnOrder state as array of column ids
  const [columnOrder, setColumnOrder] = useState(ASSESSMENT_COLUMN_ORDER);

  const table = useMaterialReactTable({
    columns,
    data, //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableGrouping: false,
    enableColumnPinning: false,
    enableFacetedValues: true,
    enableRowActions: true,
    initialState: initialState,
    enableStickyHeader: true,
    enableColumnActions: false,
    positionToolbarAlertBanner: "bottom",
    muiToolbarAlertBannerProps: {
      sx: {
        backgroundColor: "transparent !important",
        boxShadow: "none",
        color: theme.palette.commonground.contrastText,

        "&.MuiAlert-root": {
          backgroundColor: "transparent !important",
          boxShadow: "none",
        },

        "& .MuiAlert-message": {
          color: theme.palette.commonground.contrastText,
        },

        "& .MuiAlert-action": {
          color: theme.palette.secondary.main,
        },

        "& button": {
          color: `${theme.palette.secondary.main} !important`,
        },

        "& .MuiButton-root": {
          color: `${theme.palette.secondary.main} !important`,
        },
      },
    },
    rowCount,
    enableRowSelection: true,
    getRowId: row => row.id, //give each row a  useful id
    onRowSelectionChange: setRowSelection, //connect internal row selection state to your own

    onColumnOrderChange: setColumnOrder,
    defaultColumn: {
      minSize: 20, //allow columns to get smaller than default
      maxSize: 9001, //allow columns to get larger than default
      size: 50, //make columns wider by default
      grow: false,
    },
    state: {
      columnFilters,
      isLoading,
      pagination,
      // showAlertBanner: isError,
      // showProgressBars: isRefetching,
      sorting,
      columnOrder,
      rowSelection,
    },
    muiSearchTextFieldProps: {
      size: "small",
      variant: "outlined",
    },

    //#region Pagination
    paginationDisplayMode: "pages",

    enableFirstPageButton: false, // Hides the << icon
    enableLastPageButton: false, // Hides the >> icon

    manualPagination: true,
    onColumnFiltersChange: updaterOrValue => {
      setColumnFilters(prev => {
        const nextValue =
          typeof updaterOrValue === "function" ? updaterOrValue(prev) : updaterOrValue;
        return nextValue;
      });
    },
    onPaginationChange: newPagination => {
      setPagination(newPagination);
    },
    manualSorting: true,
    manualFiltering: true,
    onSortingChange: setSorting,

    muiPaginationProps: {
      color: "primary",
      shape: "rounded",
      showRowsPerPage: true,
      variant: "outlined",
    },

    //#endregion pagination
    layoutMode: "grid",
    muiTopToolbarProps: {
      sx: {
        ...mrtGetTopToolbarFlexSx(),
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.secondary.main,
        // Target the IconButton and apply styles
        "& .MuiIconButton-root": {
          marginLeft: theme.spacing(1),
          backgroundColor: theme.palette.commonground.main,
          color: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
        "& .MuiIconButton-root-active": {
          marginLeft: theme.spacing(1),
          color: theme.palette.secondary.contrastText,
          backgroundColor: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
      },
    },
    muiBottomToolbarProps: {
      sx: mrtGetBottomToolbarSx(theme),
    },
    muiTablePaperProps: {
      sx: mrtGetPaperSx(),
    },
    muiTableContainerProps: {
      ref: tableContainerRef, //get access to the table container element
      sx: mrtGetContainerSx(theme, mobileLayout),
    },
    muiTableProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.main,
      },
    },
    muiTableBodyRowProps: {
      sx: mrtGetBodyRowSx(),
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: alpha(theme.palette.commonground.contrastText, 0.12),
        color: theme.palette.commonground.contrastText,
        justifyContent: "start",
      },
    },
    displayColumnDefOptions: {
      "mrt-row-actions": {
        ...mrtGetActionColumnStyling(),
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            paddingRight: theme.spacing(2),
            borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
          },
        },
      },
      "mrt-row-select": {
        size: 50,
        grow: false,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            color: theme.palette.secondary.main,
          },
        },
      },
    },
    icons: {
      MoreHorizIcon: ({ color }) => (
        <List
          color={"secondary"}
          sx={{
            borderRadius: 16,
            width: 48,
            height: 48,
            padding: "8px",
            border: `2px solid ${theme.palette.secondary.main}`,
          }}
        />
      ),
    },
    renderRowActionMenuItems: ({ row, closeMenu }) => [
      <MenuItem
        key="view"
        onClick={() => {
          onViewConversation(row.original);
          closeMenu();
        }}
      >
        <ViewIcon fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            View Conversation
          </Typography>
        </ListItemText>
      </MenuItem>,
      <MenuItem
        key="assess"
        // Keep AI Analysis visible always, but it needs an analytics bot to run.
        // When one isn't configured (onAssessEntry is null) show it disabled
        // rather than letting the click call a null handler.
        disabled={!onAssessEntry}
        onClick={() => {
          if (onAssessEntry) {
            onAssessEntry(row.original);
          }
          closeMenu();
        }}
      >
        <Assessment fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            {onAssessEntry ? "AI Analysis" : "AI Analysis (Unavailable)"}
          </Typography>
        </ListItemText>
      </MenuItem>,
      <MenuItem
        key="compareConversations"
        // Needs both the rerun and the conversation it was rerun from
        // (integration 1.8.0+), plus an analytics bot to do the comparison.
        disabled={
          !onCompareConversations ||
          !row.original.conversationId ||
          !row.original.originalConversationId
        }
        onClick={() => {
          onCompareConversations(row.original);
          closeMenu();
        }}
      >
        <CompareArrows fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            Compare Conversations
          </Typography>
        </ListItemText>
      </MenuItem>,
      <MenuItem
        key="download"
        onClick={() => {
          onDownloadEntry(row.original);
          closeMenu();
        }}
      >
        <Download fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            Download Conversation
          </Typography>
        </ListItemText>
      </MenuItem>,
      <MenuItem
        key="edit"
        onClick={() => {
          onEditEntry(row.original);
          closeMenu();
        }}
      >
        <EditIcon fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            Edit Assessment
          </Typography>
        </ListItemText>
      </MenuItem>,
      onProgrammaticRerun && (
        <MenuItem
          key="rerun"
          onClick={() => {
            onProgrammaticRerun(row.original);
            closeMenu();
          }}
        >
          <Replay fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
          <ListItemText disableTypography={true}>
            <Typography variant="h5" color="textSecondary">
              Rerun
            </Typography>
          </ListItemText>
        </MenuItem>
      ),
      <MenuItem
        key="developer"
        onClick={() => {
          onHandleDeveloperLogs(row.original);
          closeMenu();
        }}
      >
        <LogoDev fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            Developer Logs
          </Typography>
        </ListItemText>
      </MenuItem>,
    ],
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        {sorting.length > 0 && (
          <Tooltip title="Clear All Sorting" arrow>
            <IconButton
              className="MuiIconButton-root-active"
              onClick={() => {
                setSorting([]);
              }}
            >
              <ClearSortingIcon />
            </IconButton>
          </Tooltip>
        )}
        {columnFilters.length > 0 && (
          <Tooltip title="Clear All Filters" arrow>
            <IconButton
              className="MuiIconButton-root-active"
              onClick={() => {
                setColumnFilters([]);
              }}
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
        )}
        {onRefreshTable && (
          <Tooltip title={refreshTooltipTitle} arrow>
            <span>
              <IconButton onClick={handleRefreshClick}>
                <Refresh
                  sx={{
                    animation: isRefreshSpinning ? `${spin} 0.8s linear` : "none",
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {/* eslint-disable react/jsx-pascal-case */}
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        {/* eslint-enable react/jsx-pascal-case */}
      </Box>
    ),
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          paddingTop: "4px",
          paddingLeft: 0,
          marginLeft: 0,
          flexWrap: "wrap",
        }}
      >
        <OmniResponsiveButton
          icon={<CompareArrows />}
          tooltipTitle={
            onCompareAssessments
              ? "Compare two assessments conversations using our A.I. Comparison Tool"
              : "Comparison is unavailable without an analytics bot"
          }
          buttonText="Compare"
          // Comparison uses the A.I. Comparison Tool, so it needs an analytics
          // bot; keep the button visible but disabled when one isn't configured.
          disabled={!onCompareAssessments || table.getSelectedRowModel().rows.length !== 2}
          onClick={() => {
            if (onCompareAssessments) {
              onCompareAssessments(table.getSelectedRowModel().rows);
            }
          }}
        />

        <OmniResponsiveButton
          icon={<Delete />}
          tooltipTitle="Delete the selected assessment conversations."
          buttonText="Delete"
          disabled={table.getSelectedRowModel().rows.length < 1}
          onClick={() => {
            onDeleteAssessments(table.getSelectedRowModel().rows.map(row => row.original.id));
            table.resetRowSelection();
          }}
        />
      </Box>
    ),
  });

  // Only blur/disable the table when there are genuinely no assessments to
  // show. A filter that matches nothing still leaves an active filter, so keep
  // the table interactive in that case — otherwise pointerEvents: none would
  // trap the user by disabling the "Clear filter" controls too.
  const isEmpty = !data?.length && columnFilters.length === 0;

  return (
    <Box sx={mrtGetTableWrapperSx(0, isEmpty)}>
      <DatagridTooltipProvider>
        <MaterialReactTable table={table} />
      </DatagridTooltipProvider>
    </Box>
  );
};

export default BotpressAssessmentTable;
